import { useCallback, useEffect, useRef, useState } from 'react';
import { Flag, UserMinus, UserPlus, Users } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { Select } from '@/components/ui/Select';
import { NO_HOUSE, NOT_ASSIGNED_LABEL } from '@/constants/houses';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { House, HouseStudent } from '@/types/house';
import type { InstituteClass } from '@/types/instituteClass';
import { housesApi } from '../api/houses.api';
import { MAX_ASSIGN_STUDENTS } from '../schemas/house.schema';
import {
  EMPTY_HOUSE_STUDENT_FILTERS,
  HouseStudentFilters,
  type HouseStudentFilterValues,
} from './HouseStudentFilters';
import { SetHouseModal } from './SetHouseModal';

const PAGE_SIZE = 20;

/** The member list's filters and page, as they sit in the address bar. */
type Filters = Pick<HouseStudentFilterValues, keyof HouseStudentFilterValues> & { page: number };

// No allowed lists: class and section are uuids, which the server scopes.
const DEFAULT_FILTERS: Filters = { ...EMPTY_HOUSE_STUDENT_FILTERS, page: 1 };

export interface HouseMembersPanelProps {
  /** The house shown, or `NO_HOUSE` for the students in none. */
  houseUuid: string;
  houseName: string;
  houses?: House[];
  onSelectHouse?: (houseUuid: string) => void;
  classes: InstituteClass[];
  hasPlacement: boolean;
  canManage: boolean;
  /** Bumped by the page after an assignment, so the list reads again. */
  reloadKey: number;
  /** House uuid whose assign mode should be activated (e.g. from HouseCard) */
  assignModeHouseUuid?: string | null;
  onClearAssignMode?: () => void;
  /**
   * Whether this house takes new students. False for a suspended house: its
   * members can still be seen and taken out, but the Assign buttons go.
   */
  canAssign?: boolean;
  /** Someone joined or left a house here — the page's counts are stale. */
  onChanged: () => void;
}

/**
 * The students in one house, on the Houses page itself.
 *
 * For a house: who is in it, and taking one out. When "Assign students" is
 * clicked, checkboxes appear in the table so students can be selected and added
 * inline without needing a separate modal dialog.
 */
export function HouseMembersPanel({
  houseUuid,
  houseName,
  houses = [],
  onSelectHouse,
  classes,
  hasPlacement,
  canManage,
  reloadKey,
  assignModeHouseUuid,
  onClearAssignMode,
  canAssign = true,
  onChanged,
}: HouseMembersPanelProps) {
  const isUnassigned = houseUuid === NO_HOUSE;
  const showAssign = canManage && canAssign && !isUnassigned;
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const { page } = filters;
  const setPage = useCallback((next: number) => setFilters({ page: next }), [setFilters]);
  /** The house the filters were set on — see the effect that resets them. */
  const filteredHouseRef = useRef(houseUuid);
  const [removing, setRemoving] = useState<HouseStudent | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [placing, setPlacing] = useState<HouseStudent | null>(null);

  // Checkbox assign mode states
  const [isAssigning, setIsAssigning] = useState(false);
  const [ticked, setTicked] = useState<Map<string, HouseStudent>>(new Map());
  const [isSavingAssign, setIsSavingAssign] = useState(false);

  // When assignModeHouseUuid is passed (e.g. user clicked Assign on card), activate assign mode:
  useEffect(() => {
    if (assignModeHouseUuid && assignModeHouseUuid === houseUuid) {
      setIsAssigning(true);
      setTicked(new Map());
      setPage(1);
      onClearAssignMode?.();
    }
  }, [assignModeHouseUuid, houseUuid, onClearAssignMode, setPage]);

  // When changing houses, reset assign mode & ticks. Not on arrival, which is
  // not a change of house: that would throw away the filters just restored.
  useEffect(() => {
    if (filteredHouseRef.current === houseUuid) return;
    filteredHouseRef.current = houseUuid;
    setIsAssigning(false);
    setTicked(new Map());
    setFilters(DEFAULT_FILTERS);
  }, [houseUuid, setFilters]);

  const queryHouseUuid = isAssigning ? NO_HOUSE : houseUuid;

  const { data, isLoading, error, refetch } = useApiQuery(
    () => housesApi.listStudents({ ...filters, houseUuid: queryHouseUuid, page, limit: PAGE_SIZE }),
    [queryHouseUuid, filters.search, filters.classUuid, filters.sectionUuid, page, reloadKey],
  );

  const hasFilters = Boolean(filters.search || filters.classUuid || filters.sectionUuid);

  function changed() {
    refetch();
    onChanged();
  }

  const rows = data?.items ?? [];
  const pickable = isAssigning
    ? rows.filter((row) => row.house?.uuid !== houseUuid)
    : [];
  const allOnPageTicked = pickable.length > 0 && pickable.every((row) => ticked.has(row.uuid));
  const someOnPageTicked = pickable.some((row) => ticked.has(row.uuid));
  const isOverLimit = ticked.size > MAX_ASSIGN_STUDENTS;

  function toggle(row: HouseStudent) {
    setTicked((current) => {
      const next = new Map(current);
      if (next.has(row.uuid)) next.delete(row.uuid);
      else next.set(row.uuid, row);
      return next;
    });
  }

  function toggleAllOnPage() {
    setTicked((current) => {
      const next = new Map(current);
      if (allOnPageTicked) pickable.forEach((row) => next.delete(row.uuid));
      else pickable.forEach((row) => next.set(row.uuid, row));
      return next;
    });
  }

  async function saveAssignToCurrentHouse() {
    if (ticked.size === 0 || isOverLimit || isSavingAssign) return;
    setIsSavingAssign(true);
    try {
      const result = await housesApi.assign({
        houseUuid,
        studentUuids: [...ticked.keys()],
      });
      toast.success(
        `${result.updated.toLocaleString('en-IN')} ${result.updated === 1 ? 'student' : 'students'} added to ${houseName}`,
      );
      setIsAssigning(false);
      setTicked(new Map());
      changed();
    } catch (caught) {
      toast.error(
        'Could not assign the students',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSavingAssign(false);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    const target = removing;
    setIsRemoving(true);
    try {
      await housesApi.assign({ houseUuid: null, studentUuids: [target.uuid] });
      toast.success(`${target.fullName} taken out of ${houseName}`, `Now ${NOT_ASSIGNED_LABEL}.`);
      setRemoving(null);
      changed();
    } catch (caught) {
      setRemoving(null);
      toast.error(
        `Could not take ${target.fullName} out`,
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  const showCheckboxes = canManage && isAssigning;

  const columns: Column<HouseStudent>[] = [
    ...(showCheckboxes
      ? [
          {
            key: 'tick',
            header: (
              <Checkbox
                label="Select every student on this page"
                hideLabel
                checked={allOnPageTicked}
                indeterminate={!allOnPageTicked && someOnPageTicked}
                disabled={pickable.length === 0}
                onChange={toggleAllOnPage}
              />
            ),
            render: (row: HouseStudent) => (
              <Checkbox
                label={`Select ${row.fullName}`}
                hideLabel
                checked={ticked.has(row.uuid)}
                onChange={() => toggle(row)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{row.fullName}</p>
          <p className="font-mono text-xs text-ink-3">{row.admissionNumber}</p>
        </div>
      ),
    },
    ...(hasPlacement
      ? [
          {
            key: 'class',
            header: 'Class',
            render: (row: HouseStudent) =>
              row.className ? (
                <span className="whitespace-nowrap text-sm text-ink-2">
                  {row.className}
                  {row.sectionName ? ` – ${row.sectionName}` : ''}
                </span>
              ) : (
                <span className="text-ink-3">—</span>
              ),
          },
          {
            key: 'roll',
            header: 'Roll no.',
            hideOnMobile: true,
            render: (row: HouseStudent) => (
              <span className="text-sm text-ink-2">{row.rollNumber ?? '—'}</span>
            ),
          },
        ]
      : []),
    ...(canManage && !isAssigning
      ? [
          {
            key: 'action',
            header: <span className="sr-only">Actions</span>,
            align: 'right' as const,
            render: (row: HouseStudent) =>
              isUnassigned ? (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Flag className="h-3.5 w-3.5" />}
                  onClick={() => setPlacing(row)}
                >
                  Assign house
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<UserMinus className="h-3.5 w-3.5" />}
                  aria-label={`Take ${row.fullName} out of ${houseName}`}
                  onClick={() => setRemoving(row)}
                >
                  Remove
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <section
      className="card overflow-hidden"
      aria-label={isUnassigned ? 'Students not in any house' : `Students in ${houseName}`}
    >
      <div className="border-b border-line p-4">
        <HouseStudentFilters
          values={filters}
          onChange={(next) => setFilters({ ...next, page: 1 })}
          classes={classes}
          hasPlacement={hasPlacement}
          beforeClasses={
            houses.length > 0 ? (
              <div className="w-full sm:w-40 lg:w-44 shrink-0">
                <Select
                  label="House"
                  options={[
                    ...houses.map((one) => ({
                      value: one.uuid,
                      label: one.status === 'SUSPENDED' ? `${one.name} (Suspended)` : one.name,
                    })),
                    { value: NO_HOUSE, label: NOT_ASSIGNED_LABEL },
                  ]}
                  value={houseUuid}
                  onChange={(event) => onSelectHouse?.(event.target.value)}
                />
              </div>
            ) : undefined
          }
          extra={
            isAssigning ? (
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsAssigning(false);
                    setTicked(new Map());
                  }}
                  disabled={isSavingAssign}
                >
                  Cancel
                </Button>
                {ticked.size > 0 && (
                  <Button
                    leftIcon={<UserPlus className="h-4 w-4" />}
                    onClick={() => void saveAssignToCurrentHouse()}
                    disabled={isOverLimit || isSavingAssign}
                    isLoading={isSavingAssign}
                  >
                    Assign ({ticked.size})
                  </Button>
                )}
              </div>
            ) : showAssign ? (
              <Button
                leftIcon={<UserPlus className="h-4 w-4" />}
                onClick={() => {
                  setIsAssigning(true);
                  setTicked(new Map());
                  setPage(1);
                }}
                className="text-base"
              >
                Assign students
              </Button>
            ) : undefined
          }
        />
      </div>

      {error ? (
        <div className="p-4">
          <QueryError title="Could not load the students" error={error} onRetry={refetch} />
        </div>
      ) : (
        <Table
          variant="plain"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.uuid}
          isLoading={isLoading}
          empty={
            <EmptyState
              icon={<Users className="h-5 w-5" />}
              title={
                hasFilters
                  ? 'No student matches these filters'
                  : isAssigning
                    ? 'No students found to assign'
                    : isUnassigned
                      ? 'Every student is in a house'
                      : `Nobody is in ${houseName} yet`
              }
              description={
                hasFilters
                  ? 'Clear the search or choose another class.'
                  : isAssigning
                    ? 'All students in the selected filter are already assigned.'
                    : isUnassigned
                      ? 'There is nobody left to place this year.'
                      : showAssign
                        ? 'Assign students from any class and section.'
                        : undefined
              }
              action={
                showAssign && !hasFilters && !isAssigning ? (
                  <Button
                    leftIcon={<UserPlus className="h-4 w-4" />}
                    onClick={() => {
                      setIsAssigning(true);
                      setTicked(new Map());
                      setPage(1);
                    }}
                  >
                    Assign students
                  </Button>
                ) : undefined
              }
            />
          }
        />
      )}

      {data && data.meta.totalPages > 1 && (
        <Pagination meta={data.meta} itemLabel="students" onPageChange={setPage} />
      )}

      <ConfirmDialog
        isOpen={removing !== null}
        title={`Take ${removing?.fullName ?? 'this student'} out of ${houseName}?`}
        description={`They will show as ${NOT_ASSIGNED_LABEL} until they are placed in a house again.`}
        confirmLabel="Remove from house"
        isLoading={isRemoving}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />

      <SetHouseModal
        isOpen={placing !== null}
        students={
          placing ? [{ uuid: placing.uuid, fullName: placing.fullName, houseUuid: null }] : []
        }
        onClose={() => setPlacing(null)}
        onSaved={() => {
          setPlacing(null);
          changed();
        }}
      />
    </section>
  );
}
